import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'

/**
 * Feature-flag and experiment console (part 99).
 *
 * Two ideas drive the page. A definition is configuration: kill switch, rollout percentage, hash
 * salt and an activation window expressed in Thai wall-clock time. An evaluation is a pure,
 * explainable answer for one member: the bucket is a stable hash of tenant + user + salt, so the
 * same person keeps the same bucket while the percentage moves, and an explicit deny wins even
 * when the rollout is 100%. The explanation panel shows every gate in the order the server
 * applied it, which is what makes a surprising answer diagnosable instead of mysterious.
 */

interface FlagRow {
  id: string
  tenant_id: string
  key: string
  name_th: string
  description_th: string
  enabled: boolean
  rollout_pct: number
  salt: string
  starts_at?: string
  ends_at?: string
  created_by?: string
  created_at: string
  updated_at: string
  allow_overrides: number
  deny_overrides: number
}

interface OverrideRow {
  id: string
  flag_id: string
  subject_id: string
  subject_email: string
  subject_display_name: string
  effect: 'allow' | 'deny'
  reason: string
  expires_at?: string
  created_at: string
  updated_at: string
}

interface SubjectRow {
  id: string
  email: string
  display_name: string
  role: string
}

interface EvalStep {
  stage: string
  result: string
  detail: string
}

interface Evaluation {
  flag_id: string
  flag_key: string
  subject_id: string
  value: boolean
  reason: string
  bucket: number
  threshold: number
  rollout_pct: number
  override_effect?: 'allow' | 'deny'
  in_window: boolean
  evaluated_at: string
  explanation: EvalStep[]
}

interface HistoryRow {
  id: string
  flag_id: string
  flag_key: string
  action: string
  actor_id?: string
  actor_name?: string
  before?: Record<string, unknown>
  after?: Record<string, unknown>
  note?: string
  created_at: string
}

interface Meta {
  timezone: string
  bucket_count: number
  max_rollout: number
  effects: string[]
  max_override_reason_length: number
  schedule_input: string
  reasons: { code: string; label: string }[]
}

const ROLE_LABEL: Record<string, string> = {
  owner: 'เจ้าขององค์กร',
  admin: 'ผู้ดูแลระบบ',
  audit_manager: 'ผู้จัดการตรวจสอบ',
  auditor: 'ผู้ตรวจสอบ',
  reviewer: 'ผู้สอบทาน',
  client_viewer: 'ลูกค้าอ่านอย่างเดียว',
}

const EFFECT_LABEL: Record<string, string> = {
  allow: 'อนุญาต',
  deny: 'ปฏิเสธ',
}

const REASON_LABEL: Record<string, string> = {
  kill_switch: 'สวิตช์ฉุกเฉินปิดอยู่',
  scheduled_not_started: 'ยังไม่ถึงเวลาเปิด',
  scheduled_window_ended: 'สิ้นสุดช่วงเวลาแล้ว',
  deny_override: 'คำสั่งปฏิเสธเฉพาะผู้ใช้',
  allow_override: 'คำสั่งอนุญาตเฉพาะผู้ใช้',
  rollout_zero: 'ยังไม่เปิดเปอร์เซ็นต์',
  rollout_full: 'เปิดทุกคน',
  rollout_included: 'อยู่ในกลุ่มเปอร์เซ็นต์',
  rollout_excluded: 'ไม่อยู่ในกลุ่มเปอร์เซ็นต์',
}

const ACTION_LABEL: Record<string, string> = {
  'flag.created': 'สร้างแฟล็ก',
  'flag.rollout_changed': 'ปรับเปอร์เซ็นต์',
  'flag.enabled': 'เปิดใช้งาน',
  'flag.disabled': 'ปิดฉุกเฉิน',
  'flag.schedule_changed': 'ปรับช่วงเวลา',
  'override.set': 'ตั้งคำสั่งเฉพาะผู้ใช้',
  'override.removed': 'ลบคำสั่งเฉพาะผู้ใช้',
}

const KEY_PATTERN = /^[a-z][a-z0-9_.-]{2,63}$/

const BKK_FMT = new Intl.DateTimeFormat('th-TH', {
  timeZone: 'Asia/Bangkok',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

function bkk(iso?: string | null): string {
  if (!iso) return '—'
  return BKK_FMT.format(new Date(iso))
}

function bkkInput(iso?: string | null): string {
  if (!iso) return ''
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso))
  const value = (type: string) => parts.find((part) => part.type === type)?.value ?? ''
  return `${value('year')}-${value('month')}-${value('day')}T${value('hour')}:${value('minute')}`
}

// The API reads a naive string as Bangkok wall-clock; the browser input uses the same shape with
// a T. Converting here keeps the operator's intent independent of the browser's own timezone.
function toApiTime(value: string): string {
  return value.trim() === '' ? '' : value.replace('T', ' ')
}

function scheduleText(flag: FlagRow): string {
  if (!flag.starts_at && !flag.ends_at) return 'ไม่มีกำหนด'
  const start = flag.starts_at ? bkk(flag.starts_at) : 'ทันที'
  const end = flag.ends_at ? bkk(flag.ends_at) : 'ไม่สิ้นสุด'
  return `${start} → ${end}`
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function numberTH(value: number): string {
  return value.toLocaleString('th-TH')
}

function shortId(value: string): string {
  return value.slice(0, 8)
}

interface Draft {
  key: string
  name: string
  description: string
  rollout: string
  salt: string
  startsAt: string
  endsAt: string
}

const EMPTY_DRAFT: Draft = {
  key: '',
  name: '',
  description: '',
  rollout: '0',
  salt: '',
  startsAt: '',
  endsAt: '',
}

export function FlagEngine() {
  const { me } = useAuth()
  const canWrite = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager'
  const qc = useQueryClient()

  const [selectedId, setSelectedId] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const [formError, setFormError] = useState('')
  const [notice, setNotice] = useState('')

  const meta = useQuery({
    queryKey: ['flag-engine-meta'],
    queryFn: () => api<Meta>('/v1/flag-engine/meta'),
    staleTime: Infinity,
  })

  const flags = useQuery({
    queryKey: ['flag-engine-flags'],
    queryFn: () => api<FlagRow[]>('/v1/flag-engine/flags'),
  })

  const subjects = useQuery({
    queryKey: ['flag-engine-subjects'],
    queryFn: () => api<SubjectRow[]>('/v1/flag-engine/subjects?q=&limit=200'),
    staleTime: 60_000,
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiWrite<{ flag: FlagRow }>('/v1/flag-engine/flags', 'POST', body),
    onSuccess: (saved) => {
      setCreateOpen(false)
      setDraft(EMPTY_DRAFT)
      setFormError('')
      setNotice(`สร้างแฟล็ก ${saved.flag.name_th} แล้ว`)
      setSelectedId(saved.flag.id)
      void qc.invalidateQueries({ queryKey: ['flag-engine-flags'] })
    },
    onError: (err) => setFormError(errorText(err, 'สร้างแฟล็กไม่สำเร็จ')),
  })

  const rows = flags.data ?? []
  const subjectRows = subjects.data ?? []
  const selected = rows.find((flag) => flag.id === selectedId) ?? null

  const active = useMemo(() => rows.filter((flag) => flag.enabled).length, [rows])
  const killed = rows.length - active
  const overrideTotal = rows.reduce((sum, flag) => sum + flag.allow_overrides + flag.deny_overrides, 0)
  const fullRollout = rows.filter((flag) => flag.enabled && flag.rollout_pct === 100).length

  function submitCreate() {
    setFormError('')
    const key = draft.key.trim()
    const name = draft.name.trim()
    const rollout = Number(draft.rollout)
    if (!KEY_PATTERN.test(key)) {
      setFormError('คีย์ต้องเริ่มด้วยตัวอักษรเล็ก และมีอักขระ a-z 0-9 _ . - ยาว 3 ถึง 64 ตัว')
      return
    }
    if (name === '') {
      setFormError('กรุณากรอกชื่อแฟล็ก')
      return
    }
    if (!Number.isInteger(rollout) || rollout < 0 || rollout > 100) {
      setFormError('เปอร์เซ็นต์ต้องเป็นจำนวนเต็มระหว่าง 0 ถึง 100')
      return
    }
    if (draft.startsAt && draft.endsAt && draft.endsAt <= draft.startsAt) {
      setFormError('เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่ม')
      return
    }
    create.mutate({
      key,
      name_th: name,
      description_th: draft.description.trim(),
      rollout_pct: rollout,
      salt: draft.salt.trim(),
      starts_at: toApiTime(draft.startsAt),
      ends_at: toApiTime(draft.endsAt),
    })
  }

  return (
    <>
      <PageHead
        title="ศูนย์ทดลองฟีเจอร์"
        sub="เปิดปิดฟีเจอร์และทดลองกับผู้ใช้บางส่วน · แบ่งกลุ่มด้วยแฮชขององค์กร ผู้ใช้ และเกลต์ · เวลาทั้งหมดเป็นเวลาไทย"
        actions={canWrite ? (
          <button className="btn btn-primary" onClick={() => { setCreateOpen((open) => !open); setFormError('') }}>
            {createOpen ? 'ปิดฟอร์ม' : 'สร้างแฟล็กใหม่'}
          </button>
        ) : undefined}
      />

      <div className="grid-3" style={{ marginBottom: 16 }}>
        <Stat label="แฟล็กทั้งหมด" value={rows.length} foot="นับทุกสถานะในองค์กรนี้" />
        <Stat label="เปิดใช้งาน" value={active} unit={`/ ${rows.length}`} foot={`ปิดฉุกเฉิน ${killed} รายการ`} />
        <Stat label="เปิดทุกคน" value={fullRollout} foot={`คำสั่งเฉพาะผู้ใช้ที่ยังมีผล ${overrideTotal} รายการ`} />
      </div>

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{meta.data?.timezone ?? 'Asia/Bangkok'}</span>
        <span className="chip">bucket {numberTH(meta.data?.bucket_count ?? 10000)} ค่า</span>
        <span className="chip">เปอร์เซ็นต์มีผลเมื่อไม่มีคำสั่งเฉพาะผู้ใช้</span>
        <span className="chip">คำสั่งปฏิเสธชนะ 100%</span>
      </div>

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 16 }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      {createOpen ? (
        <div className="card card-pad" style={{ marginBottom: 16 }}>
          <div className="card-label">สร้างแฟล็กใหม่</div>
          <div className="grid-2" style={{ marginTop: 12 }}>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="fe-key">คีย์ (ใช้ในโค้ด)</label>
              <input id="fe-key" className="input mono" value={draft.key} maxLength={64}
                onChange={(event) => setDraft({ ...draft, key: event.target.value })}
                placeholder="search_ranking_v2" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="fe-name">ชื่อที่แสดง</label>
              <input id="fe-name" className="input" value={draft.name} maxLength={160}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                placeholder="จัดอันดับผลค้นหารุ่นทดลอง" />
            </div>
            <div className="field" style={{ marginTop: 0, gridColumn: '1 / -1' }}>
              <label htmlFor="fe-desc">คำอธิบาย</label>
              <input id="fe-desc" className="input" value={draft.description} maxLength={1000}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                placeholder="ทดลองอันดับผลลัพธ์กับผู้ใช้บางส่วน" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="fe-rollout">เปอร์เซ็นต์เริ่มต้น</label>
              <input id="fe-rollout" className="input mono" inputMode="numeric" value={draft.rollout}
                onChange={(event) => setDraft({ ...draft, rollout: event.target.value.replace(/\D/g, '') })}
                placeholder="20" />
              <div className="dim" style={{ fontSize: 11, marginTop: 5 }}>
                0 = ยังไม่เปิดให้ใคร · 100 = ทุกคน · แบ่งกลุ่มด้วยแฮชจึงไม่สลับคนเดิมออก
              </div>
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="fe-salt">เกลต์แฮช (ไม่บังคับ)</label>
              <input id="fe-salt" className="input mono" value={draft.salt} maxLength={120}
                onChange={(event) => setDraft({ ...draft, salt: event.target.value })}
                placeholder="เว้นว่าง = ใช้คีย์เป็นเกลต์" />
              <div className="dim" style={{ fontSize: 11, marginTop: 5 }}>
                เปลี่ยนเกลต์ = สุ่มกลุ่มใหม่ทั้งชุด ควรทำเมื่อตั้งใจเริ่มการทดลองใหม่เท่านั้น
              </div>
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="fe-start">เริ่มเปิด (เวลาไทย)</label>
              <input id="fe-start" className="input mono" type="datetime-local" value={draft.startsAt}
                onChange={(event) => setDraft({ ...draft, startsAt: event.target.value })} />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="fe-end">สิ้นสุด (เวลาไทย)</label>
              <input id="fe-end" className="input mono" type="datetime-local" value={draft.endsAt}
                onChange={(event) => setDraft({ ...draft, endsAt: event.target.value })} />
            </div>
          </div>
          {formError ? <div className="form-error">{formError}</div> : null}
          <div className="row" style={{ gap: 10, marginTop: 14 }}>
            <button className="btn btn-primary" disabled={create.isPending} onClick={submitCreate}>
              {create.isPending ? 'กำลังสร้าง…' : 'สร้างแฟล็ก'}
            </button>
            <span className="dim" style={{ fontSize: 11.5 }}>
              แฟล็กใหม่เริ่มที่เปอร์เซ็นต์ที่กำหนด และยังไม่มีคำสั่งเฉพาะผู้ใช้
            </span>
          </div>
        </div>
      ) : null}

      {flags.isLoading ? (
        <div className="card card-pad"><Skel h={160} /></div>
      ) : flags.isError ? (
        <div className="form-error">{errorText(flags.error, 'โหลดรายการแฟล็กไม่สำเร็จ')}</div>
      ) : rows.length === 0 ? (
        <Empty title="ยังไม่มีแฟล็ก" body="สร้างแฟล็กแรกเพื่อเริ่มทดลองฟีเจอร์กับผู้ใช้บางส่วน" />
      ) : (
        <div className="table-wrap" style={{ marginBottom: 16 }}>
          <table className="table">
            <thead>
              <tr>
                <th>แฟล็ก</th>
                <th>สถานะ</th>
                <th className="num">เปอร์เซ็นต์</th>
                <th>ช่วงเวลาเปิด (เวลาไทย)</th>
                <th className="num">อนุญาต / ปฏิเสธ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((flag) => (
                <tr
                  key={flag.id}
                  onClick={() => setSelectedId(flag.id)}
                  style={{
                    cursor: 'pointer',
                    background: flag.id === selectedId ? 'var(--color-wash)' : undefined,
                  }}
                >
                  <td>
                    <div>{flag.name_th}</div>
                    <div className="dim mono" style={{ fontSize: 11 }}>{flag.key}</div>
                  </td>
                  <td>
                    <span className={`chip ${flag.enabled ? 'chip-on' : ''}`}>
                      {flag.enabled ? 'เปิดใช้งาน' : 'ปิดฉุกเฉิน'}
                    </span>
                  </td>
                  <td className="num">{flag.rollout_pct}%</td>
                  <td style={{ fontSize: 11.5 }}>{scheduleText(flag)}</td>
                  <td className="num">{flag.allow_overrides} / {flag.deny_overrides}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected ? (
        <FlagDetail
          key={selected.id}
          flag={selected}
          subjects={subjectRows}
          canWrite={canWrite}
          onNotice={setNotice}
        />
      ) : rows.length > 0 ? (
        <div className="card card-pad">
          <span className="dim" style={{ fontSize: 12 }}>เลือกแฟล็กจากตารางเพื่อดูการตั้งค่า คำอธิบายผลประเมิน และประวัติ</span>
        </div>
      ) : null}
    </>
  )
}

function FlagDetail({
  flag,
  subjects,
  canWrite,
  onNotice,
}: {
  flag: FlagRow
  subjects: SubjectRow[]
  canWrite: boolean
  onNotice: (message: string) => void
}) {
  const qc = useQueryClient()
  const [rollout, setRollout] = useState(String(flag.rollout_pct))
  const [enabled, setEnabled] = useState(flag.enabled)
  const [salt, setSalt] = useState(flag.salt)
  const [startsAt, setStartsAt] = useState(bkkInput(flag.starts_at))
  const [endsAt, setEndsAt] = useState(bkkInput(flag.ends_at))
  const [clearSchedule, setClearSchedule] = useState(false)
  const [note, setNote] = useState('')
  const [configError, setConfigError] = useState('')

  const [evalSubject, setEvalSubject] = useState('')
  const [overrideSubject, setOverrideSubject] = useState('')
  const [overrideEffect, setOverrideEffect] = useState<'allow' | 'deny'>('deny')
  const [overrideReason, setOverrideReason] = useState('')
  const [overrideExpiry, setOverrideExpiry] = useState('')
  const [overrideError, setOverrideError] = useState('')

  const overrides = useQuery({
    queryKey: ['flag-engine-overrides', flag.id],
    queryFn: () => api<OverrideRow[]>(`/v1/flag-engine/flags/${encodeURIComponent(flag.id)}/overrides?limit=200`),
  })

  const history = useQuery({
    queryKey: ['flag-engine-history', flag.id],
    queryFn: () => api<HistoryRow[]>(`/v1/flag-engine/flags/${encodeURIComponent(flag.id)}/history?limit=100`),
  })

  const evaluation = useQuery({
    queryKey: ['flag-engine-evaluation', flag.id, evalSubject],
    queryFn: () => api<{ evaluation: Evaluation }>(
      `/v1/flag-engine/flags/${encodeURIComponent(flag.id)}/evaluate?subject_id=${encodeURIComponent(evalSubject)}`,
    ),
    enabled: evalSubject !== '',
  })

  useEffect(() => {
    if (evalSubject === '' && subjects.length > 0) setEvalSubject(subjects[0].id)
    if (overrideSubject === '' && subjects.length > 0) setOverrideSubject(subjects[0].id)
  }, [subjects, evalSubject, overrideSubject])

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiWrite<{ flag: FlagRow }>(`/v1/flag-engine/flags/${encodeURIComponent(flag.id)}/rollout`, 'PATCH', body),
    onSuccess: (saved) => {
      setEnabled(saved.flag.enabled)
      setRollout(String(saved.flag.rollout_pct))
      setSalt(saved.flag.salt)
      setStartsAt(bkkInput(saved.flag.starts_at))
      setEndsAt(bkkInput(saved.flag.ends_at))
      setClearSchedule(false)
      setNote('')
      setConfigError('')
      onNotice(`บันทึกการตั้งค่า ${saved.flag.name_th} แล้ว`)
      void qc.invalidateQueries({ queryKey: ['flag-engine-flags'] })
      void qc.invalidateQueries({ queryKey: ['flag-engine-history', flag.id] })
      void qc.invalidateQueries({ queryKey: ['flag-engine-evaluation', flag.id] })
    },
    onError: (err) => setConfigError(errorText(err, 'บันทึกการตั้งค่าไม่สำเร็จ')),
  })

  const setOverride = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiWrite<{ override: OverrideRow }>(`/v1/flag-engine/flags/${encodeURIComponent(flag.id)}/overrides`, 'POST', body),
    onSuccess: () => {
      setOverrideReason('')
      setOverrideExpiry('')
      setOverrideError('')
      onNotice('บันทึกคำสั่งเฉพาะผู้ใช้แล้ว')
      void qc.invalidateQueries({ queryKey: ['flag-engine-overrides', flag.id] })
      void qc.invalidateQueries({ queryKey: ['flag-engine-history', flag.id] })
      void qc.invalidateQueries({ queryKey: ['flag-engine-flags'] })
      void qc.invalidateQueries({ queryKey: ['flag-engine-evaluation', flag.id] })
    },
    onError: (err) => setOverrideError(errorText(err, 'บันทึกคำสั่งเฉพาะผู้ใช้ไม่สำเร็จ')),
  })

  const removeOverride = useMutation({
    mutationFn: (subjectId: string) =>
      apiWrite<{ status: string }>(
        `/v1/flag-engine/flags/${encodeURIComponent(flag.id)}/overrides/${encodeURIComponent(subjectId)}`,
        'DELETE',
      ),
    onSuccess: () => {
      setOverrideError('')
      onNotice('ลบคำสั่งเฉพาะผู้ใช้แล้ว ผู้ใช้กลับไปใช้เปอร์เซ็นต์ปกติ')
      void qc.invalidateQueries({ queryKey: ['flag-engine-overrides', flag.id] })
      void qc.invalidateQueries({ queryKey: ['flag-engine-history', flag.id] })
      void qc.invalidateQueries({ queryKey: ['flag-engine-flags'] })
      void qc.invalidateQueries({ queryKey: ['flag-engine-evaluation', flag.id] })
    },
    onError: (err) => setOverrideError(errorText(err, 'ลบคำสั่งเฉพาะผู้ใช้ไม่สำเร็จ')),
  })

  function saveConfig() {
    setConfigError('')
    const pct = Number(rollout)
    if (!Number.isInteger(pct) || pct < 0 || pct > 100) {
      setConfigError('เปอร์เซ็นต์ต้องเป็นจำนวนเต็มระหว่าง 0 ถึง 100')
      return
    }
    if (!clearSchedule && startsAt && endsAt && endsAt <= startsAt) {
      setConfigError('เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่ม')
      return
    }
    const body: Record<string, unknown> = {
      rollout_pct: pct,
      enabled,
      salt,
      note: note.trim(),
    }
    if (clearSchedule) {
      body.clear_schedule = true
    } else {
      body.starts_at = toApiTime(startsAt)
      body.ends_at = toApiTime(endsAt)
    }
    update.mutate(body)
  }

  function submitOverride() {
    setOverrideError('')
    if (overrideSubject === '') {
      setOverrideError('กรุณาเลือกผู้ใช้')
      return
    }
    setOverride.mutate({
      subject_id: overrideSubject,
      effect: overrideEffect,
      reason: overrideReason.trim(),
      expires_at: toApiTime(overrideExpiry),
    })
  }

  const overrideRows = overrides.data ?? []
  const historyRows = history.data ?? []
  const result = evaluation.data?.evaluation
  const subjectName = subjects.find((subject) => subject.id === evalSubject)
  const now = Date.now()

  return (
    <>
      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div className="card-label">การตั้งค่าแฟล็ก</div>
            <h2 style={{ margin: '6px 0 2px' }}>{flag.name_th}</h2>
            <div className="dim mono" style={{ fontSize: 12 }}>{flag.key} · {shortId(flag.id)}</div>
          </div>
          <span className={`chip ${flag.enabled ? 'chip-on' : ''}`}>
            {flag.enabled ? 'เปิดใช้งาน' : 'สวิตช์ฉุกเฉินปิดอยู่'}
          </span>
        </div>
        {flag.description_th ? (
          <div className="dim" style={{ fontSize: 12, marginTop: 10 }}>{flag.description_th}</div>
        ) : null}

        <div className="grid-2" style={{ marginTop: 14 }}>
          <div className="field" style={{ marginTop: 0 }}>
            <label htmlFor="fe-edit-rollout">เปอร์เซ็นต์การเปิดใช้</label>
            <input id="fe-edit-rollout" className="input mono" inputMode="numeric" value={rollout}
              onChange={(event) => setRollout(event.target.value.replace(/\D/g, ''))} />
            <div className="dim" style={{ fontSize: 11, marginTop: 5 }}>
              เกณฑ์คือ {numberTH(Number(rollout && !Number.isNaN(Number(rollout)) ? Number(rollout) : 0) * 100)} จาก{" "}
              {numberTH(10000)} · ผู้ที่ bucket ต่ำกว่าเกณฑ์จะอยู่ในกลุ่ม
            </div>
          </div>
          <div className="field" style={{ marginTop: 0 }}>
            <label htmlFor="fe-edit-salt">เกลต์แฮช</label>
            <input id="fe-edit-salt" className="input mono" value={salt} maxLength={120}
              onChange={(event) => setSalt(event.target.value)} />
            <div className="dim" style={{ fontSize: 11, marginTop: 5 }}>
              เปลี่ยนเกลต์แล้วบันทึก = จัดกลุ่มใหม่ทั้งชุด
            </div>
          </div>
          <div className="field" style={{ marginTop: 0 }}>
            <label htmlFor="fe-edit-start">เริ่มเปิด (เวลาไทย)</label>
            <input id="fe-edit-start" className="input mono" type="datetime-local" value={startsAt}
              disabled={clearSchedule} onChange={(event) => setStartsAt(event.target.value)} />
          </div>
          <div className="field" style={{ marginTop: 0 }}>
            <label htmlFor="fe-edit-end">สิ้นสุด (เวลาไทย)</label>
            <input id="fe-edit-end" className="input mono" type="datetime-local" value={endsAt}
              disabled={clearSchedule} onChange={(event) => setEndsAt(event.target.value)} />
          </div>
          <div className="field" style={{ marginTop: 0, gridColumn: '1 / -1' }}>
            <label htmlFor="fe-edit-note">หมายเหตุการเปลี่ยนแปลง (ไม่บังคับ)</label>
            <input id="fe-edit-note" className="input" value={note} maxLength={1000}
              onChange={(event) => setNote(event.target.value)}
              placeholder="เพิ่มเปอร์เซ็นต์หลังตรวจผลสัปดาห์แรก" />
          </div>
          <div className="field" style={{ marginTop: 0, gridColumn: '1 / -1' }}>
            <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={enabled} disabled={!canWrite}
                onChange={(event) => setEnabled(event.target.checked)} />
              เปิดใช้งานแฟล็ก (ยกเลิก = สวิตช์ฉุกเฉิน ปิดทุกคนทันที)
            </label>
            <label className="row" style={{ gap: 8, cursor: 'pointer', marginTop: 8 }}>
              <input type="checkbox" checked={clearSchedule} disabled={!canWrite}
                onChange={(event) => setClearSchedule(event.target.checked)} />
              ลบช่วงเวลาเปิดใช้งาน (เปิดตลอดจนกว่าจะปิดเอง)
            </label>
          </div>
        </div>
        {configError ? <div className="form-error">{configError}</div> : null}
        <div className="row" style={{ gap: 10, marginTop: 14 }}>
          <button className="btn btn-primary" disabled={!canWrite || update.isPending} onClick={saveConfig}>
            {update.isPending ? 'กำลังบันทึก…' : 'บันทึกการตั้งค่า'}
          </button>
          <span className="dim" style={{ fontSize: 11.5 }}>
            ทุกการเปลี่ยนแปลงถูกบันทึกในประวัติ และในสาย audit ขององค์กร
          </span>
        </div>
      </div>

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="card-label">อธิบายผลประเมินสำหรับผู้ใช้</div>
        <div className="row" style={{ gap: 10, marginTop: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ marginTop: 0, minWidth: 260 }}>
            <label htmlFor="fe-eval-subject">ผู้ใช้</label>
            <select id="fe-eval-subject" className="select" style={{ width: '100%' }} value={evalSubject}
              onChange={(event) => setEvalSubject(event.target.value)}>
              <option value="">เลือกผู้ใช้</option>
              {subjects.map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.display_name} · {subject.email}
                </option>
              ))}
            </select>
          </div>
          <span className="dim" style={{ fontSize: 11.5, paddingBottom: 10 }}>
            ผลนี้คำนวณสดจาก tenant + user + salt ไม่ได้บันทึกเป็นแถว
          </span>
        </div>

        {evaluation.isError ? (
          <div className="form-error">{errorText(evaluation.error, 'ประเมินผลไม่สำเร็จ')}</div>
        ) : result ? (
          <div style={{ marginTop: 12 }}>
            <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <span className={`chip ${result.value ? 'chip-on' : ''}`} style={{ fontSize: 14 }}>
                {result.value ? 'เปิดให้ผู้ใช้รายนี้' : 'ปิดสำหรับผู้ใช้รายนี้'}
              </span>
              <span className="chip">{REASON_LABEL[result.reason] ?? result.reason}</span>
              <span className="chip">bucket {numberTH(result.bucket)} / {numberTH(10000)}</span>
              <span className="chip">เกณฑ์ {numberTH(result.threshold)} ({result.rollout_pct}%)</span>
              <span className="chip">{result.in_window ? 'อยู่ในช่วงเวลา' : 'นอกช่วงเวลา'}</span>
            </div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
              ประเมินเมื่อ {bkk(result.evaluated_at)} · {subjectName?.display_name ?? shortId(result.subject_id)}
            </div>
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="table">
                <thead>
                  <tr><th>ขั้นตอน</th><th>ผล</th><th>รายละเอียด</th></tr>
                </thead>
                <tbody>
                  {result.explanation.map((step, index) => (
                    <tr key={`${step.stage}-${index}`}>
                      <td style={{ whiteSpace: 'nowrap' }}>{step.stage}</td>
                      <td>{step.result}</td>
                      <td className="dim" style={{ fontSize: 11.5 }}>{step.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : evalSubject === '' ? (
          <div className="dim" style={{ fontSize: 12, marginTop: 12 }}>เลือกผู้ใช้เพื่อดูคำอธิบายทีละขั้น</div>
        ) : (
          <div className="dim" style={{ fontSize: 12, marginTop: 12 }}>กำลังประเมิน…</div>
        )}
      </div>

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="card-label">คำสั่งเฉพาะผู้ใช้</div>
        <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>
          คำสั่งปฏิเสธมีผลเหนือเปอร์เซ็นต์ทุกค่า รวมถึง 100% · คำสั่งอนุญาตเปิดให้ผู้ใช้แม้เปอร์เซ็นต์เป็น 0
        </div>

        {overrideRows.length === 0 ? (
          <div className="dim" style={{ fontSize: 12, marginTop: 12 }}>ยังไม่มีคำสั่งเฉพาะผู้ใช้สำหรับแฟล็กนี้</div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>ผู้ใช้</th>
                  <th>คำสั่ง</th>
                  <th>เหตุผล</th>
                  <th>หมดอายุ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {overrideRows.map((override) => {
                  const expired = override.expires_at ? new Date(override.expires_at).getTime() <= now : false
                  return (
                    <tr key={override.id}>
                      <td>
                        <div>{override.subject_display_name}</div>
                        <div className="dim" style={{ fontSize: 11 }}>{override.subject_email}</div>
                      </td>
                      <td>
                        <span className={`chip ${override.effect === 'deny' ? '' : 'chip-on'}`}>
                          {EFFECT_LABEL[override.effect] ?? override.effect}
                        </span>
                      </td>
                      <td className="dim" style={{ fontSize: 11.5 }}>{override.reason || '—'}</td>
                      <td style={{ fontSize: 11.5 }}>
                        {override.expires_at ? bkk(override.expires_at) : 'ถาวร'}
                        {expired ? <span className="dim"> · หมดอายุแล้ว</span> : null}
                      </td>
                      <td className="num">
                        <button className="btn btn-ghost" disabled={!canWrite || removeOverride.isPending}
                          onClick={() => removeOverride.mutate(override.subject_id)}>
                          ลบ
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {canWrite ? (
          <div className="grid-2" style={{ marginTop: 14 }}>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="fe-ov-subject">ผู้ใช้</label>
              <select id="fe-ov-subject" className="select" style={{ width: '100%' }} value={overrideSubject}
                onChange={(event) => setOverrideSubject(event.target.value)}>
                <option value="">เลือกผู้ใช้</option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.display_name} · {ROLE_LABEL[subject.role] ?? subject.role}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="fe-ov-effect">คำสั่ง</label>
              <select id="fe-ov-effect" className="select" style={{ width: '100%' }} value={overrideEffect}
                onChange={(event) => setOverrideEffect(event.target.value === 'allow' ? 'allow' : 'deny')}>
                <option value="deny">ปฏิเสธ (ชนะทุกเปอร์เซ็นต์)</option>
                <option value="allow">อนุญาต (ชนะเปอร์เซ็นต์ 0)</option>
              </select>
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="fe-ov-reason">เหตุผล</label>
              <input id="fe-ov-reason" className="input" value={overrideReason} maxLength={500}
                onChange={(event) => setOverrideReason(event.target.value)}
                placeholder="ลูกค้าขอปิดการทดลองสำหรับผู้ใช้รายนี้" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="fe-ov-expiry">หมดอายุ (เวลาไทย, ไม่บังคับ)</label>
              <input id="fe-ov-expiry" className="input mono" type="datetime-local" value={overrideExpiry}
                onChange={(event) => setOverrideExpiry(event.target.value)} />
            </div>
          </div>
        ) : null}
        {overrideError ? <div className="form-error">{overrideError}</div> : null}
        {canWrite ? (
          <div className="row" style={{ gap: 10, marginTop: 12 }}>
            <button className="btn btn-primary" disabled={setOverride.isPending} onClick={submitOverride}>
              {setOverride.isPending ? 'กำลังบันทึก…' : 'บันทึกคำสั่ง'}
            </button>
            <span className="dim" style={{ fontSize: 11.5 }}>
              เว้นวันหมดอายุ = คำสั่งถาวรจนกว่าจะลบ
            </span>
          </div>
        ) : null}
      </div>

      <div className="card card-pad">
        <div className="card-label">ประวัติการตั้งค่า</div>
        {historyRows.length === 0 ? (
          <div className="dim" style={{ fontSize: 12, marginTop: 10 }}>ยังไม่มีประวัติสำหรับแฟล็กนี้</div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 10 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>เวลา (ไทย)</th>
                  <th>การกระทำ</th>
                  <th>ผู้ทำ</th>
                  <th>หมายเหตุ</th>
                </tr>
              </thead>
              <tbody>
                {historyRows.map((entry) => (
                  <tr key={entry.id}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>{bkk(entry.created_at)}</td>
                    <td>{ACTION_LABEL[entry.action] ?? entry.action}</td>
                    <td style={{ fontSize: 11.5 }}>{entry.actor_name || shortId(entry.actor_id ?? '') || 'ระบบ'}</td>
                    <td className="dim" style={{ fontSize: 11.5 }}>{entry.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
